package link.erpango.quip.features.settings

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch
import link.erpango.quip.domain.entities.DUser
import link.erpango.quip.core.TokenRefreshManager
import link.erpango.quip.domain.usecases.IAuthUseCase
import link.erpango.quip.domain.usecases.IGarageUseCase
import link.erpango.quip.utils.exportData
import link.erpango.quip.utils.FileUtils

data class SettingsState(
    val user: DUser? = null,
    val pressureUnit: PressureUnit = PressureUnit.BAR,
    val notificationsEnabled: Boolean = true,
    val itvNotificationsEnabled: Boolean = true,
    val revisionNotificationsEnabled: Boolean = true,
    val sparePartNotificationsEnabled: Boolean = true,
    val currentTheme: String = "Obsidian",
    val appVersion: String = "1.0.0"
)

enum class PressureUnit {
    BAR, PSI
}

sealed class SettingsEvent {
    object None : SettingsEvent()
    object SignOutCompleted : SettingsEvent()
    data class TokenRefreshed(val success: Boolean) : SettingsEvent()
}

class SettingsViewModel(
    private val authInteractor: IAuthUseCase,
    private val garageInteractor: IGarageUseCase,
    private val tokenRefreshManager: TokenRefreshManager? = null
) : ViewModel() {

    private val _settingsState = MutableStateFlow(SettingsState())
    val settingsState: StateFlow<SettingsState> = _settingsState.asStateFlow()

    private val _settingsEvent = MutableStateFlow<SettingsEvent>(SettingsEvent.None)
    val settingsEvent: StateFlow<SettingsEvent> = _settingsEvent.asStateFlow()

    init {
        loadSettings()
    }

    private fun loadSettings() {
        viewModelScope.launch {
            val user = garageInteractor.getLoggedUser()
            val savedTheme = garageInteractor.getSavedTheme() ?: "Classic Mechanic"

            // Show local values immediately for instant UI
            val localMaintenance = authInteractor.getNotificationSettings()
            val localItv = authInteractor.getItvNotificationSettings()
            val localRevision = authInteractor.getRevisionNotificationSettings()
            val localSparePart = authInteractor.getSparePartNotificationSettings()
            _settingsState.value = _settingsState.value.copy(
                user = user,
                appVersion = garageInteractor.getAppVersion() ?: "0.0.0",
                currentTheme = savedTheme,
                notificationsEnabled = localMaintenance,
                itvNotificationsEnabled = localItv,
                revisionNotificationsEnabled = localRevision,
                sparePartNotificationsEnabled = localSparePart
            )

            // Sync from server and update if different
            val remote = authInteractor.fetchAndSyncNotificationSettings()
            if (remote != null) {
                _settingsState.value = _settingsState.value.copy(
                    notificationsEnabled = remote.notificationsEnabled,
                    itvNotificationsEnabled = remote.itvNotificationsEnabled,
                    revisionNotificationsEnabled = remote.revisionNotificationsEnabled,
                    sparePartNotificationsEnabled = remote.sparePartNotificationsEnabled
                )
            }
        }
    }

    fun signOut() {
        viewModelScope.launch {
            // 1. Cerrar sesión en Firebase
            authInteractor.signOut(viewModelScope)
            
            // 2. Borrar TODOS los datos locales (vehículos, mantenimientos, repuestos, usuario, tokens)
            authInteractor.deleteAllLocalData()
            
            // 3. Emitir evento de cierre de sesión completado para navegar al login
            _settingsEvent.value = SettingsEvent.SignOutCompleted
        }
    }

    fun onEventHandled() {
        _settingsEvent.value = SettingsEvent.None
    }

    fun updatePressureUnit(unit: PressureUnit) {
        _settingsState.value = _settingsState.value.copy(pressureUnit = unit)
        // TODO: Guardar en preferencias persistentes
    }

    fun toggleNotifications() {
        viewModelScope.launch {
            val newValue = !_settingsState.value.notificationsEnabled
            _settingsState.value = _settingsState.value.copy(notificationsEnabled = newValue)
            authInteractor.updateNotificationSettings(
                newValue,
                _settingsState.value.itvNotificationsEnabled,
                _settingsState.value.revisionNotificationsEnabled,
                _settingsState.value.sparePartNotificationsEnabled
            )
        }
    }

    fun toggleItvNotifications() {
        viewModelScope.launch {
            val newValue = !_settingsState.value.itvNotificationsEnabled
            _settingsState.value = _settingsState.value.copy(itvNotificationsEnabled = newValue)
            authInteractor.updateNotificationSettings(
                _settingsState.value.notificationsEnabled,
                newValue,
                _settingsState.value.revisionNotificationsEnabled,
                _settingsState.value.sparePartNotificationsEnabled
            )
        }
    }

    fun toggleRevisionNotifications() {
        viewModelScope.launch {
            val newValue = !_settingsState.value.revisionNotificationsEnabled
            _settingsState.value = _settingsState.value.copy(revisionNotificationsEnabled = newValue)
            authInteractor.updateNotificationSettings(
                _settingsState.value.notificationsEnabled,
                _settingsState.value.itvNotificationsEnabled,
                newValue,
                _settingsState.value.sparePartNotificationsEnabled
            )
        }
    }

    fun toggleSparePartNotifications() {
        viewModelScope.launch {
            val newValue = !_settingsState.value.sparePartNotificationsEnabled
            _settingsState.value = _settingsState.value.copy(sparePartNotificationsEnabled = newValue)
            authInteractor.updateNotificationSettings(
                _settingsState.value.notificationsEnabled,
                _settingsState.value.itvNotificationsEnabled,
                _settingsState.value.revisionNotificationsEnabled,
                newValue
            )
        }
    }
    
    fun updateTheme(themeName: String) {
        val scheme = link.erpango.quip.theme.ColorSchemes.allSchemes[themeName] ?: return
        
        // Update global QuipColors state using the new updateScheme method
        link.erpango.quip.theme.QuipColors.updateScheme(scheme)
        
        _settingsState.value = _settingsState.value.copy(currentTheme = themeName)
        
        viewModelScope.launch {
            garageInteractor.saveTheme(themeName) // Use existing garageInteractor
        }
    }

    fun exportAllData(onResult: (String) -> Unit) {
        viewModelScope.launch {
            val allVehicles = garageInteractor.getFullGarage()
            val data = exportData(allVehicles)
            onResult(data)
        }
    }

    fun shareLogFile() {
        FileUtils.shareLogFile()
    }

    fun refreshToken() {
        viewModelScope.launch {
            val success = tokenRefreshManager?.tryRefreshToken() ?: false
            _settingsEvent.value = SettingsEvent.TokenRefreshed(success)
        }
    }
}
